import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Star, Sparkles } from "lucide-react";
import { IMAGES } from "../data/site";
import { scrollToId } from "./Header";
import { trackEvent } from "../utils/analytics";

const ease = [0.16, 1, 0.3, 1];
const rise = (delay) => ({
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease },
});

export const Hero = () => (
  <section className="relative overflow-hidden bg-white" data-testid="hero-section">
    {/* soft ice-blue depth */}
    <div className="absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-brand-ice blur-3xl opacity-70" aria-hidden="true" />
    <div className="absolute top-1/2 -left-40 h-96 w-96 rounded-full bg-brand-iceBg blur-3xl" aria-hidden="true" />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 md:pt-40 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
      {/* COPY */}
      <div className="lg:col-span-6">
        <motion.p {...rise(0.1)} className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-blue" data-testid="hero-overline">
          Fönsterputs & utvändig rengöring · Hela Skaraborg
        </motion.p>
        <motion.h1
          {...rise(0.22)}
          className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-brand-ink"
          data-testid="hero-headline"
        >
          Fönsterputs som gör{" "}
          <span className="font-accent italic font-semibold text-brand-blue">skillnad.</span>
        </motion.h1>
        <motion.p {...rise(0.34)} className="mt-6 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-xl" data-testid="hero-subtext">
          Se Klart Ut hjälper privatpersoner och företag i hela Skaraborg. Från rena fönster till solpaneler, altaner och markytor – vi ser till att utsidan av din fastighet håller sig ren, fräsch och välskött.
        </motion.p>
        <motion.div {...rise(0.46)} className="mt-9 flex flex-col sm:flex-row gap-4">
          <Link
            to="/boka-offert"
            data-testid="hero-cta-button"
            onClick={() => trackEvent("boka_click", { location: "hero" })}
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue text-white font-semibold px-8 py-4 hover:bg-brand-blueHover hover:-translate-y-0.5 hover:shadow-[0_14px_35px_rgba(14,98,254,0.35)] active:translate-y-0 transition-all duration-300"
          >
            Boka fönsterputs
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <button
            onClick={() => scrollToId("#sa-fungerar-det")}
            data-testid="hero-secondary-button"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-brand-ink border border-slate-200 font-semibold px-8 py-4 hover:border-brand-blue hover:text-brand-blue transition-all duration-300"
          >
            Så fungerar det
          </button>
        </motion.div>
        <motion.div {...rise(0.58)} className="mt-10 flex items-center gap-3" data-testid="hero-social-proof">
          <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />)}</div>
          <span className="text-sm text-slate-500">Älskade av kunder i hela Skaraborg</span>
        </motion.div>
      </div>

      {/* LAYERED VISUAL */}
      <div className="lg:col-span-6 relative">
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5, ease }}
          className="relative"
        >
          <div className="overflow-hidden rounded-[2.5rem] shadow-[0_40px_80px_-20px_rgba(7,22,44,0.3)] ring-1 ring-slate-900/5 group">
            <img
              src={IMAGES.workTwo}
              alt="Se Klart Ut – skinande rent fönster efter professionell putsning"
              className="w-full aspect-[4/5] max-h-[620px] object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
              data-testid="hero-image"
            />
            {/* glass reflection sweep */}
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-white/25 pointer-events-none" aria-hidden="true" />
          </div>

          {/* floating badge: rating */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0, ease }}
            className="absolute left-3 md:-left-8 top-5 md:top-8 rounded-2xl bg-white/90 backdrop-blur-md px-4 py-3 md:px-5 md:py-4 shadow-[0_8px_30px_rgb(7,22,44,0.12)] border border-white/60"
            data-testid="hero-badge-rating"
          >
            <div className="flex items-center gap-1">{[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />)}</div>
            <p className="mt-1.5 text-xs font-semibold text-brand-ink">Rekommenderade i Skaraborg</p>
          </motion.div>

          {/* floating badge: insured */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.15, ease }}
            className="absolute right-3 md:-right-6 bottom-5 md:bottom-10 rounded-2xl bg-brand-ink text-white px-4 py-3 md:px-5 md:py-4 shadow-[0_8px_30px_rgb(7,22,44,0.25)] flex items-center gap-3 max-w-[calc(100%-2rem)]"
            data-testid="hero-badge-insured"
          >
            <ShieldCheck className="h-5 w-5 text-brand-ice" strokeWidth={1.75} />
            <div>
              <p className="text-xs font-semibold">Försäkrat & garanterat</p>
              <p className="text-[11px] text-white/60">Se Klart Ut-garanti ingår alltid</p>
            </div>
          </motion.div>

          {/* animated sparkle indicator */}
          <motion.div
            animate={{ scale: [1, 1.15, 1], opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-5 right-5 md:top-6 md:right-8 h-10 w-10 md:h-11 md:w-11 rounded-full bg-brand-blue text-white flex items-center justify-center shadow-[0_8px_25px_rgba(14,98,254,0.45)]"
            aria-hidden="true"
          >
            <Sparkles className="h-5 w-5" />
          </motion.div>
        </motion.div>
      </div>
    </div>
  </section>
);

export default Hero;
